import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CButton, CCol, CRow } from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslationContext } from "@/context/TranslationContext";
import { breadcrumb_key } from "@/constants/translation/common.translation";
import { communityLeaderboard } from "@/constants/translation/communityLeaderboard.translation";
import { masters } from "@/libs/routes";
import { useCommunityLeaderboardList } from "@/hooks/useCommunityLeaderboard";
import { TNTable } from "@/components";
import CustomDropdown from "@/components/CustomDropdown";
import constants from "@/libs/constant";

const SCOPE_FILTER_VALUES = ["city", "country", "global"];

const CommunityLeaderboardList = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();

  const [leaderboardList, setLeaderboardList] = useState([]);
  const [paginationData, setPaginationData] = useState(null);

  const [currentPage, setCurrentPage] = useState(constants.pagination.defaultPage);
  const [scopeFilter, setScopeFilter] = useState("global");
  const [tableKey, setTableKey] = useState(0);

  const isFiltering = useRef(false);

  const handleScopeChange = (e) => {
    isFiltering.current = true;
    setScopeFilter(e.target.value);
    setCurrentPage(1);
    setTableKey((prev) => prev + 1);

    setTimeout(() => {
      isFiltering.current = false;
    }, 200);
  };

  const {
    data: listData,
    isLoading,
  } = useCommunityLeaderboardList({
    scope: scopeFilter,
    page: currentPage,
    limit: constants.pagination.defaultPageSize,
  });

  useEffect(() => {
    if (listData) {
      setLeaderboardList(listData.data?.leaderboardList || []);
      setPaginationData(listData.data?.pagination);
    }
  }, [listData]);

  const scopeOptions = useMemo(
    () =>
      SCOPE_FILTER_VALUES.map((value) => ({
        value,
        label: t(communityLeaderboard.list.scope_filter[value]),
      })),
    [t],
  );

  const handleViewClick = (referralUuid) => {
    if (!referralUuid) {
      return;
    }
    navigate(masters.referralRecords.view.replace(":id", referralUuid));
  };

  const columns = useMemo(
    () => [
      {
        id: "rank",
        accessorKey: "rank",
        header: t(communityLeaderboard.list.table.rank_col_heading),
        cell: ({ getValue }) => <span>{getValue() ?? t(communityLeaderboard.list.defaults.na)}</span>,
      },
      {
        id: "user_name",
        accessorKey: "user_name",
        header: t(communityLeaderboard.list.table.user_name_col_heading),
        cell: ({ getValue }) => <span>{getValue() || t(communityLeaderboard.list.defaults.na)}</span>,
      },
      {
        id: "email",
        accessorKey: "email",
        header: t(communityLeaderboard.list.table.email_col_heading),
        cell: ({ getValue }) => <span>{getValue() || t(communityLeaderboard.list.defaults.na)}</span>,
      },
      {
        id: "city",
        accessorKey: "city",
        header: t(communityLeaderboard.list.table.city_col_heading),
        // No fallback "Germany" for city (unlike country) - a blank city just means the user
        // never set one, shown as a plain dash rather than the translated "N/A" used elsewhere.
        cell: ({ getValue }) => <span>{getValue() || "-"}</span>,
      },
      {
        id: "country",
        accessorKey: "country",
        header: t(communityLeaderboard.list.table.country_col_heading),
        cell: ({ getValue }) => <span>{getValue() || t(communityLeaderboard.list.defaults.na)}</span>,
      },
      {
        id: "total_successful_referrals",
        accessorKey: "total_successful_referrals",
        header: t(communityLeaderboard.list.table.total_successful_referrals_col_heading),
        cell: ({ getValue }) => <span>{getValue() ?? t(communityLeaderboard.list.defaults.na)}</span>,
      },
      {
        id: "action",
        accessorKey: "latest_referral_uuid",
        header: t(communityLeaderboard.list.table.action_col_heading),
        cell: ({ row }) => {
          const latestReferralUuid = row.original.latest_referral_uuid;
          const isDisabled = !latestReferralUuid;
          return (
            <div className="d-flex justify-content-center gap-3">
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="community-leaderboard-view-tooltip">
                    {isDisabled
                      ? t(communityLeaderboard.list.no_referral_tooltip)
                      : t(communityLeaderboard.list.view_tooltip)}
                  </Tooltip>
                }
              >
                <span>
                  <CButton
                    disabled={isDisabled}
                    onClick={() => handleViewClick(latestReferralUuid)}
                    style={{
                      backgroundColor: "#212631",
                      borderColor: "#212631",
                      color: "white",
                      padding: "0.25rem 0.5rem",
                      fontSize: "0.875rem",
                      opacity: isDisabled ? 0.5 : 1,
                    }}
                  >
                    <FontAwesomeIcon icon={faEye} />
                  </CButton>
                </span>
              </OverlayTrigger>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  return (
    <>
      <CRow className="mb-4">
        <CCol lg={12} className="d-flex gap-2 justify-content-end flex-wrap">
          <CustomDropdown
            className="form-control-sm"
            style={{ maxWidth: "200px", backgroundColor: "#212631", borderColor: "#212631", color: "white" }}
            value={scopeFilter}
            onChange={handleScopeChange}
            options={scopeOptions}
          />
        </CCol>
      </CRow>

      <TNTable
        key={tableKey}
        columns={columns}
        data={leaderboardList}
        paginationData={paginationData}
        onSelectPage={(pageIndex) => {
          if (isFiltering.current) {
            return;
          }
          setCurrentPage(pageIndex + 1);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        t={t}
        pageIndexGet={currentPage - 1}
        isLoading={isLoading}
      />
    </>
  );
};

CommunityLeaderboardList.breadCrumbArray = [
  {
    name: breadcrumb_key.masters?.communityLeaderboard?.list,
    pathname: masters.communityLeaderboard.list,
    active: true,
  },
];

export default CommunityLeaderboardList;
